import React, { useState } from 'react';
import { X, Upload, FileArchive, DollarSign } from 'lucide-react';

const MixMasterModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    instructions: ''
  });
  const [selectedService, setSelectedService] = useState('standard'); // 'standard' or 'rush'
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const amount = selectedService === 'rush' ? 150 : 100;
      const hostUrl = window.location.origin;

      // Create a temporary booking for mix/master service
      const bookingResponse = await fetch(`/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studio_id: 1,
          customer_name: formData.name,
          customer_email: formData.email,
          booking_date: new Date().toISOString(),
          duration_hours: 1
        })
      });

      const bookingData = await bookingResponse.json();

      // Create Stripe checkout
      const paymentResponse = await fetch(`/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingData.booking_id,
          payment_type: 'full',
          host_url: hostUrl
        })
      });

      const paymentData = await paymentResponse.json();

      // Redirect to Stripe checkout
      window.location.href = paymentData.checkout_url;
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-gradient-to-br from-purple-900/40 to-blue-900/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-purple-500/30">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">Mix & Master Service</h2>
            <p className="text-gray-400 text-sm">Full payment required - Upload stems after payment</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-purple-900/30 hover:bg-purple-900/50 flex items-center justify-center transition-colors"
          >
            <X className="text-white" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Pricing Info */}
          <div className="bg-gradient-to-br from-green-900/20 to-blue-900/20 border border-green-500/30 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-white font-bold text-lg">Standard Mix & Master</h3>
                <p className="text-gray-400 text-sm">Per song</p>
              </div>
              <div className="text-3xl font-bold text-green-400">$100</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center space-x-2 text-gray-300">
                <span className="text-green-400">✓</span>
                <span>Full digital mix</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300">
                <span className="text-green-400">✓</span>
                <span>Industry-standard master</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300">
                <span className="text-green-400">✓</span>
                <span>Vocal cleanup + tuning</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300">
                <span className="text-green-400">✓</span>
                <span>2 free revisions</span>
              </div>
            </div>
          </div>

          {/* Service Selection */}
          <div className="mb-6 space-y-3">
            <button
              type="button"
              onClick={() => setSelectedService('standard')}
              className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                selectedService === 'standard'
                  ? 'border-green-500 bg-green-900/20'
                  : 'border-gray-700 bg-gray-900/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-semibold">Standard Mix & Master</h4>
                  <p className="text-gray-400 text-sm">Standard turnaround</p>
                </div>
                <div className="text-xl font-bold text-green-400">$100</div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setSelectedService('rush')}
              className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                selectedService === 'rush'
                  ? 'border-yellow-500 bg-yellow-900/20'
                  : 'border-gray-700 bg-gray-900/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-semibold">Rush Service (24 Hours)</h4>
                  <p className="text-gray-400 text-sm">Priority turnaround</p>
                </div>
                <div className="text-xl font-bold text-yellow-400">$150</div>
              </div>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              placeholder="Your Name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
            />
            <input
              type="email"
              placeholder="Email Address"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
            />
            <input
              type="tel"
              placeholder="Phone Number"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
            />
            <textarea
              placeholder="Special instructions for mixing/mastering (optional)..."
              rows={4}
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-none"
            ></textarea>

            {/* Upload Info */}
            <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <FileArchive className="text-purple-400 mt-1 flex-shrink-0" size={20} />
                <div className="text-sm text-gray-300">
                  <p className="font-semibold text-white mb-1">After Payment:</p>
                  <p>Upload your stems as a ZIP file. We'll send you an upload link via email.</p>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isProcessing}
              className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <DollarSign size={20} />
                  <span>Pay ${selectedService === 'rush' ? '150' : '100'}</span>
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-4">
            Full payment required. Secure payment processing.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MixMasterModal;
